/**
 * HustlePointsTermsPage — /legal/hustlepoints-terms
 *
 * Canonical program terms for Hustle Points. Every sales page links
 * here. Reviewed by an AU consumer/financial-services solicitor prior
 * to launch (placeholder date below — update after review).
 */
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { ShieldCheck } from "lucide-react";

export default function HustlePointsTermsPage() {
  return (
    <div className="min-h-screen bg-stone-50" data-testid="points-terms-page">
      <PlatformHeader brand={{ name: "Hustle Points", to: "/points" }} showBookCta={false} showCart={false} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="text-amber-500" size={26}/>
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            Program Terms · Version 1.0 · Effective 9 Feb 2026
          </p>
        </div>
        <h1 className="text-4xl md:text-5xl font-black mb-6 text-stone-900">Hustle Points Program Terms</h1>

        <div className="prose prose-stone max-w-none prose-headings:font-black prose-headings:text-stone-900 prose-p:text-stone-700 prose-li:text-stone-700 prose-a:text-amber-600">

          <h2>1. What Hustle Points are</h2>
          <p>
            Hustle Points ("<strong>Points</strong>") are a promotional loyalty reward
            offered by <strong>HustleSuite Pty Ltd</strong> ABN [tbc]
            ("<strong>HustleSuite</strong>", "we", "us", "our") to members of the
            HustleSuite ecosystem (HustlePlaza, HustleMeet, HustleStadium,
            HustleTickets, HustleEvents, HustleStay, HustleConnect and related
            services — collectively the "<strong>Program</strong>").
          </p>
          <p>
            <strong>Points do not constitute money, stored value, a non-cash
            payment facility, or a stored-value facility</strong> under the
            <em> Corporations Act 2001</em> (Cth), the <em>Banking Act 1959</em> (Cth),
            or any successor regime. Points are a contractual right to redeem
            for eligible goods and services at Participating Hustle Vendors,
            not a debt payable in Australian dollars.
          </p>

          <h2>2. Earning Points</h2>
          <p>You can earn Points by:</p>
          <ul>
            <li><strong>Top-up</strong>: purchasing Points direct from HustleSuite. The
              standard rate is <strong>$1 AUD = 1.1 Points</strong> (a 10% loyalty
              bonus), rounded to the nearest whole Point.</li>
            <li><strong>Vendor spend</strong>: spending at a Participating Hustle Vendor.
              The standard earn rate is <strong>1 Point per $1 spent</strong>, unless a
              higher rate is advertised for a specific promotion.</li>
            <li><strong>Program activities</strong>: referrals, event attendance,
              product reviews, and other activities we may add from time to
              time (each with its own advertised Point value).</li>
            <li><strong>Gift Pack</strong>: claiming a Hustle Points Gift Pack purchased
              by another member.</li>
          </ul>

          <h2>3. Redeeming Points</h2>
          <p>
            Points are redeemable at Participating Hustle Vendors listed at{" "}
            <a href="/vendors">hustlesuite.au/vendors</a> at the rate of{" "}
            <strong>10 Points = $1 AUD of eligible goods or services</strong>.
            Redemption is at the vendor's point of sale and is subject to the
            vendor confirming the redemption.
          </p>

          <h2>4. Restrictions</h2>
          <ul>
            <li>Points are <strong>non-transferable</strong> except via a Hustle Points
              Gift Pack purchased through the Program.</li>
            <li>Points are <strong>non-refundable</strong> and cannot be exchanged for
              cash, cheque, bank deposit, or any other cash equivalent under any
              circumstance.</li>
            <li>Points cannot be sold, bartered, or transferred for consideration
              outside the Program.</li>
            <li>Points redemptions are subject to the terms and conditions of the
              relevant Participating Hustle Vendor for the goods or services
              redeemed.</li>
          </ul>

          <h2>5. Expiry &amp; forfeiture</h2>
          <p>
            <strong>Account inactivity:</strong> If your HustleSuite account has no Point-
            earning or Point-redeeming activity for <strong>24 consecutive months</strong>,
            your entire Point balance will be automatically forfeited without
            further notice or compensation.
          </p>
          <p>
            <strong>Hustle Points Gift Packs:</strong> Unclaimed Gift Packs expire{" "}
            <strong>24 months</strong> after the date of purchase. Once expired, the
            Points are forfeited and cannot be reissued. Gift Packs are governed
            by these Program Terms and are <strong>not gift cards</strong> for the
            purposes of Part 3-2A of the Australian Consumer Law.
          </p>
          <p>
            <strong>Traditional gift cards</strong> (denominated in AUD) sold by
            HustleSuite are covered by the separate Gift Card Terms available at{" "}
            <a href="/legal/gift-card-terms">hustlesuite.au/legal/gift-card-terms</a>{" "}
            and carry a minimum three-year expiry as required by section 99A of
            the Australian Consumer Law.
          </p>

          <h2>6. Program changes</h2>
          <p>
            HustleSuite may, acting reasonably, adjust the earn rate, redemption
            rate, list of Participating Hustle Vendors, or any other term of the
            Program on <strong>30 days' notice</strong> given by email or through the
            Program interface. Continued use of the Program after the change
            takes effect constitutes acceptance.
          </p>
          <p>
            If a change materially reduces the value of Points already in your
            account, we will honour redemptions at the pre-change rate for a
            transition period of at least 60 days.
          </p>

          <h2>7. No cash-out; no chargebacks</h2>
          <p>
            Because Points are a loyalty reward and not stored value, they
            cannot be converted back to money. Purchases of Points via Stripe
            Checkout are non-refundable once Points have been credited. Members
            who initiate a chargeback for a completed Point purchase will have
            the corresponding Points reversed from their account and may be
            suspended from the Program.
          </p>

          <h2>8. Fraud, abuse &amp; suspension</h2>
          <p>
            HustleSuite may suspend or terminate any account, and forfeit any
            associated Points, where we reasonably suspect fraud, misuse of the
            Program (including collusive vendor redemptions), breach of these
            Terms, or breach of law.
          </p>

          <h2>9. Tax</h2>
          <p>
            The GST treatment of Point redemptions follows ATO Ruling GSTR
            2003/5 — GST is applied to the underlying goods or services
            supplied by the vendor at the AUD-equivalent redemption value, not
            to the issuance of the Points themselves.
          </p>
          <p>Members are responsible for any personal income-tax consequences of
            receiving or redeeming Points (typically none for consumer
            redemptions but consult your tax adviser for business use).</p>

          <h2>10. Consumer law rights</h2>
          <p>
            Nothing in these Terms excludes, restricts or modifies your rights
            under the Australian Consumer Law or any other consumer-protection
            legislation. Where a term of these Terms would otherwise be void as
            an unfair contract term, it is read down to the extent necessary to
            preserve validity.
          </p>

          <h2>11. Privacy</h2>
          <p>
            Your use of the Program is subject to our <a href="/privacy">Privacy
            Policy</a>. We share Point balance and redemption data with
            Participating Hustle Vendors solely to facilitate the redemption
            you request.
          </p>

          <h2>12. Governing law</h2>
          <p>
            These Terms are governed by the laws of Queensland, Australia. The
            parties submit to the non-exclusive jurisdiction of the courts of
            Queensland.
          </p>

          <h2>13. Contact</h2>
          <p>
            Questions about the Program? Email <a href="mailto:hello@hustlesuite.au">hello@hustlesuite.au</a>{" "}
            or write to HustleSuite Pty Ltd, [registered address].
          </p>

          <p className="mt-8 text-xs text-stone-500">
            Program Terms v1.0 — 9 February 2026. Draft — subject to legal review
            prior to public launch. Contact your solicitor before relying on
            these terms in commercial dealings.
          </p>
        </div>
      </div>
      <SuiteFooter />
    </div>
  );
}
